import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import { TEAM, ADVISORY, SITE } from "@/data/content";
import { Target, Binoculars, ShieldCheck, Star } from "lucide-react";

export default function About() {
  return (
    <>
      <SEO
        title="About Veterans for Indiana | Leadership Through Service"
        description="Learn about Veterans for Indiana — our mission, vision, values, team, and advisory council of leaders dedicated to electing veterans and military families across Indiana."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Veterans for Indiana",
          url: "https://www.veteransforindiana.com/about",
        }}
      />

      {/* TOP BANNER */}
      <section className="relative bg-navy-deep text-white py-8 overflow-hidden">
        <div className="absolute inset-0 pattern-stars opacity-90" />
        <div className="container-x relative flex items-center justify-center gap-4 sm:gap-8 text-center">
          <StarTriad />
          <p className="font-display tracking-wider text-sm sm:text-base lg:text-lg uppercase text-white">
            Service didn&apos;t end with the uniform. Leadership continues here at home.
          </p>
          <StarTriad />
        </div>
      </section>

      {/* MISSION / VISION / VALUES */}
      <section className="bg-cream py-20" aria-labelledby="mvv-title">
        <div className="container-x">
          <Reveal className="text-center">
            <h1 id="mvv-title" className="h-display text-4xl sm:text-5xl scroll-mt-28">Our Mission, Vision &amp; Values</h1>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-10 lg:gap-14">
            <PillarCard
              icon={Target}
              title="Our Mission"
              body="Recruit, support, and elect veterans and military family members to public office — from school board to statewide office."
              delay={1}
            />
            <PillarCard
              icon={Binoculars}
              title="Our Vision"
              body="An Indiana where those who served have a strong voice in our communities and our government at every level."
              delay={2}
            />
            <Reveal delay={3} className="text-center md:border-l border-gray-300 md:pl-10">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-brand text-white">
                <ShieldCheck size={28} />
              </div>
              <h3 className="font-display tracking-widest text-navy uppercase mt-5 text-lg">Our Values</h3>
              <ul className="mt-4 space-y-2 text-navy-deep/85 text-left inline-block">
                {[
                  "Service Above Self",
                  "Integrity in Leadership",
                  "Country and Community",
                  "Respect and Accountability",
                  "Unity Through Service",
                ].map((v) => (
                  <li key={v} className="flex items-center gap-3">
                    <Star size={14} className="fill-green-brand text-green-brand shrink-0" />
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="mt-16 max-w-3xl mx-auto border-t border-green-brand/40" />
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-white py-20" aria-labelledby="team-title">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 id="team-title" className="h-display text-3xl sm:text-4xl scroll-mt-28">Our Team</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={(i + 1)}>
                <PersonCard person={m} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ADVISORY */}
      <section className="bg-gray-soft py-20" aria-labelledby="adv-title">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 id="adv-title" className="h-display text-3xl sm:text-4xl">Advisory Council</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ADVISORY.map((m, i) => (
              <Reveal key={m.name} delay={((i % 4) + 1)}>
                <AdvisorCard person={m} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-navy-deep text-white py-16 overflow-hidden">
        <div className="absolute inset-0 pattern-stars opacity-80" />
        <div className="container-x relative grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <h2 className="h-display text-3xl sm:text-4xl text-white">Help Us Build a Stronger Indiana.</h2>
            <p className="mt-4 text-white/85 max-w-xl">Donate, volunteer, or join our network of veterans, families, and supporters.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <a href={SITE.donateUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="about-donate-cta">Donate</a>
            <a href="/get-involved" className="btn btn-ghost-light" data-testid="about-join-cta">Join Our Network</a>
          </div>
        </div>
      </section>
    </>
  );
}

function PillarCard({ icon: Icon, title, body, delay }) {
  return (
    <Reveal delay={delay} className="text-center md:border-l border-gray-300 md:pl-10 first:md:border-l-0 first:md:pl-0">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-brand text-white">
        <Icon size={28} />
      </div>
      <h3 className="font-display tracking-widest text-navy uppercase mt-5 text-lg">{title}</h3>
      <p className="mt-4 text-navy-deep/80 leading-relaxed">{body}</p>
    </Reveal>
  );
}

function PersonCard({ person }) {
  return (
    <article className="card-lift bg-white rounded-lg overflow-hidden shadow-sm ring-1 ring-gray-200">
      <div className="aspect-[4/5] bg-gray-soft overflow-hidden">
        <PhotoOrFallback src={person.photo} alt={`Veterans for Indiana team member ${person.name}`} initials={initials(person.name)} />
      </div>
      <div className="p-5 text-center border-t border-gray-100">
        <h3 className="font-display text-xl text-navy uppercase tracking-wide">{person.name}</h3>
        <p className="mt-1 font-display text-xs tracking-[0.22em] uppercase text-green-brand">{person.title}</p>
        <span className="mt-3 inline-block h-[2px] w-10 bg-green-brand" />
      </div>
    </article>
  );
}

function AdvisorCard({ person }) {
  return (
    <article className="card-lift bg-white rounded-lg overflow-hidden shadow-sm ring-1 ring-gray-200 h-full flex flex-col">
      <div className="aspect-square bg-navy-deep overflow-hidden">
        <PhotoOrFallback src={person.photo} alt={`Veterans for Indiana advisory council member ${person.name}`} initials={initials(person.name)} dark />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-lg text-navy uppercase tracking-wide">{person.name}</h3>
        <span className="mt-2 inline-block h-[2px] w-10 bg-green-brand" />
        <p className="mt-3 text-sm text-navy-deep/80 leading-relaxed flex-1">{person.bio}</p>
      </div>
    </article>
  );
}

function PhotoOrFallback({ src, alt, initials, dark }) {
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
        onError={(e) => {
          // graceful fallback if image fails
          e.currentTarget.style.display = "none";
          if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex";
        }}
      />
    );
  }
  return <Fallback initials={initials} dark={dark} />;
}

function Fallback({ initials, dark }) {
  return (
    <div
      className={`h-full w-full flex items-center justify-center font-display text-5xl tracking-widest ${dark ? "text-white bg-navy-deep" : "text-navy bg-gray-soft"}`}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function StarTriad() {
  return (
    <div className="flex gap-1 text-green-brand" aria-hidden="true">
      <Star size={14} className="fill-green-brand" />
      <Star size={14} className="fill-green-brand" />
      <Star size={14} className="fill-green-brand" />
    </div>
  );
}
